import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UpdateUserParams,
  UpdateUserBody,
  UpdateUserData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UpdateUserState
  extends MergeRequestState<UpdateUserData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.updateUser,
  isFetching: (state: ReduxState) => state.server.updateUser.isFetching,
  data: (state: ReduxState) => state.server.updateUser.data,
  error: (state: ReduxState) => state.server.updateUser.error,
};

const initialState: UpdateUserState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/updateUser';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      body,
      config,
    }: {
      params: UpdateUserParams;
      body: UpdateUserBody;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) => serverApi.updateUser(params, body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUpdateUser {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const updateUser: ServerUpdateUser = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
